import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Swords, Trophy } from "lucide-react";
import api from "@/lib/api";
import PublicShell from "@/components/PublicShell";
import { BattleLogFeed } from "@/components/BattleLogFeed";
import { FightTypeSummaryCard } from "@/components/FightTypeSummaryCard";
import { usePageMeta } from "@/lib/seo";
import { useAuth } from "@/context/AuthContext";
import BattleReplay from "@/components/BattleReplay";
import { warmBattle3DResources } from "@/lib/battle3dWarmup";

export default function PublicBattle() {
  const { id } = useParams();
  const [b, setB] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    api.get(`/public/battle/${id}`).then((r) => setB(r.data)).catch(() => setB(false));
  }, [id]);

  useEffect(() => {
    warmBattle3DResources();
  }, []);

  const canonical = typeof window !== "undefined" ? `${window.location.origin}/b/${id}` : undefined;
  const title = b && b.g1 ? `${b.g1} vs ${b.g2} — Arena Result | Lanista Manager` : "Arena Result | Lanista Manager";
  const description = b && b.g1
    ? `${(b.summary || b.headline || `${b.g1} faced ${b.g2} in the arena.`).slice(0, 180)}${b.winner_name ? ` Victor: ${b.winner_name}.` : ""} Watch the full play-by-play on Lanista Manager, the free online gladiator game.`
    : "Watch this arena bout on Lanista Manager, the free online gladiator game.";
  const jsonLd = b && b.g1 ? {
    "@context": "https://schema.org",
    "@type": "SportsEvent",
    "name": `${b.g1} vs ${b.g2}`,
    "description": description,
    "url": canonical,
    "competitor": [{ "@type": "Person", "name": b.g1 }, { "@type": "Person", "name": b.g2 }],
  } : null;
  usePageMeta({ title, description, canonical, jsonLd });

  return (
    <PublicShell>
      {b === null ? (
        <p className="text-center text-zinc-500 py-20 font-cinzel">Recounting the bout…</p>
      ) : b === false ? (
        <div className="text-center py-20">
          <p className="text-zinc-400 mb-6">This bout could not be found in the records.</p>
          <Link to={user ? "/" : "/login"} className="font-cinzel uppercase tracking-wide text-sm bg-amber-600 text-zinc-950 px-5 py-2.5 hover:bg-amber-500 transition-colors">
            {user ? "Return to Ludus" : "Enter the Arena"}
          </Link>
        </div>
      ) : (
        <Bout b={b} user={user} />
      )}
    </PublicShell>
  );
}

function Bout({ b, user }) {
  const navigate = useNavigate();
  const isLoggedIn = !!user;
  const g1Won = b.winner_id && b.winner_id === b.g1_id;
  const [replayOpen, setReplayOpen] = useState(false);
  return (
    <div data-testid="public-battle">
      {isLoggedIn && (
        <button
          onClick={() => {
            if (window.history.length > 1) {
              navigate(-1);
            } else {
              navigate("/");
            }
          }}
          className="mb-4 inline-flex items-center gap-1.5 font-cinzel uppercase tracking-wider text-xs border border-zinc-800 bg-zinc-950 hover:border-amber-600 hover:text-amber-300 px-4 py-2 transition-colors rounded-sm"
        >
          ← Go Back
        </button>
      )}
      <div className="text-center border border-amber-800/40 bg-gradient-to-br from-amber-950/20 to-zinc-950 p-8 mb-6">
        <div className="font-cinzel uppercase tracking-widest text-[11px] text-amber-500/80 mb-4">Arena Result</div>
        <div className="flex items-center justify-center gap-4 sm:gap-8">
          <div className={`flex-1 text-right ${g1Won ? "text-amber-300" : "text-zinc-500"}`}>
            <div className="font-cinzel text-xl sm:text-3xl uppercase tracking-wide truncate">{b.g1}</div>
            {b.g1_owner_name && <div className="text-[11px] text-zinc-600">{b.g1_owner_name}&apos;s Ludus</div>}
          </div>
          <Swords size={28} className="text-zinc-600 shrink-0" />
          <div className={`flex-1 text-left ${!g1Won ? "text-amber-300" : "text-zinc-500"}`}>
            <div className="font-cinzel text-xl sm:text-3xl uppercase tracking-wide truncate">{b.g2}</div>
            {b.g2_owner_name && <div className="text-[11px] text-zinc-600">{b.g2_owner_name}&apos;s Ludus</div>}
          </div>
        </div>
        {b.winner_name && (
          <div className="mt-5 inline-flex items-center gap-2 text-amber-300 font-cinzel uppercase tracking-wide text-sm border-y border-amber-900/40 px-4 py-2">
            <Trophy size={16} className="text-amber-500" /> {b.winner_name} wins
          </div>
        )}
        {b.headline && <p className="mt-4 font-cinzel italic text-amber-400/90 text-base sm:text-lg">“{b.headline}”</p>}
        <FightTypeSummaryCard fightType={b.fight_type} dataTestId="public-fight-type" />
        <div className="mt-5">
          <button
            data-testid="public-battle-3d-btn"
            onClick={() => setReplayOpen(true)}
            onMouseEnter={() => warmBattle3DResources()}
            onFocus={() => warmBattle3DResources()}
            className="recruit-pulse inline-flex items-center gap-2 font-cinzel uppercase tracking-wide text-xs border border-amber-600/70 bg-amber-950/25 px-4 py-2 hover:border-amber-500 hover:text-amber-200 transition-colors rounded-sm"
          >
            Watch Battle in 3D
          </button>
        </div>
      </div>

      {b.log && b.log.length > 0 && (
        <>
          <h2 className="font-cinzel text-amber-300 uppercase tracking-wide text-sm mb-3">Play-by-Play</h2>
          <div className="mb-8">
            <BattleLogFeed lines={b.log} dataTestId="public-battle-log" />
          </div>
        </>
      )}

      <div className="border border-amber-700/40 bg-gradient-to-br from-amber-950/30 to-zinc-950 p-6 text-center" data-testid="public-cta">
        <h3 className="font-cinzel text-xl sm:text-2xl text-amber-300 uppercase tracking-wide mb-2">
          {isLoggedIn ? "Step back into the arena" : "Step into the arena"}
        </h3>
        <p className="text-sm text-zinc-400 max-w-md mx-auto mb-5">
          {isLoggedIn 
            ? "Your gladiators are waiting for your command. Return to your ludus to manage schedules, forge gear, and challenge rivals."
            : "Build a ludus, train gladiators, and write your own headlines. Free to play."}
        </p>
        <Link to={isLoggedIn ? "/" : "/login"} data-testid="public-signup-cta"
          className="inline-flex items-center gap-2 font-cinzel uppercase tracking-wide text-sm bg-amber-600 text-zinc-950 px-6 py-3 hover:bg-amber-500 transition-colors">
          <Swords size={16} /> {isLoggedIn ? "Return to Ludus" : "Create Your Ludus"}
        </Link>
      </div>

      <BattleReplay
        battle={b}
        open={replayOpen}
        onClose={() => setReplayOpen(false)}
        title="Arena Result"
        shareable
        startIn3D
      />
    </div>
  );
}
